import { useCallback, useEffect, useState } from 'react';
import { CircleAlertIcon } from 'lucide-react';
import classnames from 'classnames';

import MainView from '../../components/main-view/main-view';
import { paymentsSDK } from '../../webservices/payments-api';

import styles from './redeem-gift-card.module.scss';
import VerifyGiftCardCard from './components/verify-gift-card-card';
import { GiftCardProductDetails, GiftCardUser } from './types';
import BeneficiaryCard from './components/beneficiary-card';
import RedeemingCard from './components/redeeming-card';

const RedeemGiftCardView = () => {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [details, setDetails] = useState<GiftCardProductDetails>();
    const [user, setUser] = useState<GiftCardUser>();
    const [redeemed, setRedeemed] = useState(false);
    const [error, setError] = useState({ shown: false, message: '' });

    useEffect(() => {
        currentStep > 2 && redeem();
    }, [currentStep]);

    const verifyGiftCard = async (code: string) => {
        if (!code) return;
        setLoading(true);
        const productDetails = await paymentsSDK.giftCards.verify(code.trim());
        setLoading(false);
        setDetails(productDetails);
    };

    const redeem: () => Promise<void> = useCallback(async () => {
        if (!code || !user) return;
        setError({ shown: false, message: '' });
        const response = await paymentsSDK.giftCards.redeem({ code, user });
        if (response.error) return showError(response.error);
        setRedeemed(response.success);
    }, [code, user]);

    const showError: (message: string) => void = useCallback((message) => {
        setError({ shown: true, message });
        setCurrentStep(1); // Reset flow
        setDetails(undefined);
        // Hide error toast
        setTimeout(() => {
            setError((prev) => ({ ...prev, shown: false }));
        }, 3000);
    }, []);

    const goToStep: (step: number) => void = useCallback(
        (step) => {
            if (redeemed) return;
            setCurrentStep(step);
        },
        [redeemed]
    );

    const goHome = () => (window.location.href = 'https://home.panatech.io');

    return (
        <MainView>
            <div className={styles.cardsList}>
                <VerifyGiftCardCard
                    currentStep={currentStep}
                    loading={loading}
                    details={details}
                    onVerify={verifyGiftCard}
                    onContinue={(code) => {
                        setCurrentStep(currentStep + 1);
                        setCode(code);
                    }}
                    onClear={() => setDetails(undefined)}
                    onEdit={goToStep}
                />
                <BeneficiaryCard
                    currentStep={currentStep}
                    onContinue={(user) => {
                        setCurrentStep(currentStep + 1);
                        setUser(user);
                    }}
                />
                <RedeemingCard
                    currentStep={currentStep}
                    success={redeemed}
                    onGoHome={goHome}
                />
            </div>
            <div
                className={classnames({
                    [styles.errorMessage]: true,
                    [styles.shown]: error.shown,
                })}
            >
                <CircleAlertIcon color="#fc7b7b" />
                <span>{error.message}</span>
            </div>
        </MainView>
    );
};

export default RedeemGiftCardView;
